import React from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectCallRecordTransferredFrom } from 'flow/selectors/call-records-selectors';
import { CallRecordKey } from 'flow/types/EntityKeys';
import ContactTitle from './ContactTitle';
import ContactSubtitle from './ContactSubtitle';

interface Props {
    callRecordKey: CallRecordKey;
    compact?: boolean;
    // Adds the number and company after the name, the way the caller line does
    // on the detail page. List rows are one line each and leave it off.
    showContactDetails?: boolean;
}

/**
 * Names whoever attended-transferred the call to the viewer, shaped like the
 * "To <queue>" line above it. The record's own number is the party actually
 * spoken to, so without this the handover is invisible in the history.
 */
const TransferredByInfo = ({
    callRecordKey,
    compact,
    showContactDetails,
}: Props) => {
    const { i18next } = useAppContext();
    // Undefined for every call that wasn't transferred, which is most of them.
    const transferredFrom = useAppSelector((state) =>
        selectCallRecordTransferredFrom(state, callRecordKey),
    );

    if (!transferredFrom) return null;

    const contact = transferredFrom.get('contact');
    const number = transferredFrom.get('number');
    const contactFullName = [
        contact?.get('firstName'),
        contact?.get('lastName'),
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <span className={compact ? 'block truncate' : ''}>
            {i18next.t('call.transferred_by')}{' '}
            <span className="text-main">
                <ContactTitle
                    contact={contact}
                    contactFullName={contactFullName}
                    number={number}
                    hideIcon={true}
                />
            </span>
            {showContactDetails && (
                <>
                    {' '}
                    <ContactSubtitle
                        contact={contact}
                        contactFullName={contactFullName}
                        number={number}
                        inline={true}
                    />
                </>
            )}
        </span>
    );
};

export default TransferredByInfo;
